import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Pencil, Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  hasTabContent,
  MAX_PLAN_ROUTE_TABS,
  PlanRouteTab,
  tabLabel,
} from "@/components/analyzeOrderOnPage/planRouteTabs"

type TabNameInputProps = {
  initialValue: string
  // undefined when the edit was cancelled
  onDone: (name: string | undefined) => void
}

const TabNameInput = ({ initialValue, onDone }: TabNameInputProps) => {
  const { t } = useTranslation()
  const [value, setValue] = useState(initialValue)
  // Enter and Escape unmount the input, which fires a blur as well.
  const doneRef = useRef(false)
  const finish = (name: string | undefined) => {
    if (doneRef.current) return
    doneRef.current = true
    onDone(name)
  }

  return (
    <input
      autoFocus
      aria-label={t("planOrder.tabs.routeName")}
      value={value}
      maxLength={60}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault()
          finish(value)
        } else if (e.key === "Escape") {
          e.preventDefault()
          e.stopPropagation()
          finish(undefined)
        }
      }}
      onBlur={() => finish(value)}
      className="mx-1 h-6 w-44 rounded bg-background px-1.5 text-sm outline-none ring-1 ring-ring"
    />
  )
}

type PlanRouteTabBarProps = {
  tabs: PlanRouteTab[]
  activeTabId: string
  canAdd: boolean
  onSelect: (tabId: string) => void
  onAdd: () => void
  onClose: (tabId: string) => void
  onRename: (tabId: string, name: string) => void
}

export const PlanRouteTabBar: React.FC<PlanRouteTabBarProps> = ({
  tabs,
  activeTabId,
  canAdd,
  onSelect,
  onAdd,
  onClose,
  onRename,
}) => {
  const { t } = useTranslation()
  const [editingTabId, setEditingTabId] = useState<string | undefined>()
  // A single empty tab has nothing to close.
  const closable = tabs.length > 1 || hasTabContent(tabs[0])

  return (
    <div className="flex min-w-0 items-center gap-1">
      <div
        role="tablist"
        aria-label={t("planOrder.tabs.plannedRoutes")}
        className="flex min-w-0 items-center gap-1 overflow-x-auto overscroll-x-contain py-0.5"
      >
        {tabs.map((tab) => {
          const label = tabLabel(tab, t)
          const active = tab.id === activeTabId
          const editing = editingTabId === tab.id
          return (
            <div
              key={tab.id}
              className={cn(
                "group flex h-8 max-w-[220px] shrink-0 items-center rounded-md border text-sm transition-colors",
                active
                  ? "border-border bg-background font-medium text-foreground shadow-sm"
                  : "border-transparent bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              {editing ? (
                <TabNameInput
                  initialValue={label}
                  onDone={(name) => {
                    setEditingTabId(undefined)
                    // Confirming the suggested label keeps it following the
                    // route; only a changed name is pinned.
                    if (name !== undefined && name.trim() !== label) {
                      onRename(tab.id, name)
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={active}
                  title={t("planOrder.tabs.tabTitle", { label })}
                  className="min-w-0 truncate py-1 pl-2.5 pr-1 text-left"
                  onClick={() => onSelect(tab.id)}
                  onDoubleClick={() => setEditingTabId(tab.id)}
                >
                  {label}
                </button>
              )}
              {active && !editing && (
                <button
                  type="button"
                  aria-label={t("planOrder.tabs.renameTab", { label })}
                  title={t("planOrder.tabs.rename")}
                  className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={() => setEditingTabId(tab.id)}
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
              {closable && !editing && (
                <button
                  type="button"
                  aria-label={t("planOrder.tabs.closeTab", { label })}
                  title={t("common.close")}
                  className={cn(
                    "mr-1 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground",
                    !active &&
                      "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
                  )}
                  onClick={() => onClose(tab.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )
        })}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label={t("planOrder.tabs.newRoute")}
        title={
          canAdd
            ? t("planOrder.tabs.newRoute")
            : t("planOrder.tabs.maxRoutes", { count: MAX_PLAN_ROUTE_TABS })
        }
        disabled={!canAdd}
        onClick={onAdd}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  )
}
